import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Mail, Lock, Eye, EyeOff } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { useToast } from '../hooks/use-toast';
import { authService } from '../utils/apiService';

const Login = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      console.log('🔐 Intentando login con:', email);
      console.log('📍 URL del backend:', process.env.REACT_APP_BACKEND_URL);
      
      const response = await authService.login({ email, password });
      console.log('✅ Login exitoso:', response);
      
      toast({
        title: '¡Bienvenido!',
        description: 'Has iniciado sesión correctamente.',
      });
      
      // Pequeño delay antes de navegar
      setTimeout(() => {
        navigate('/dashboard');
      }, 500);
      
    } catch (error) {
      console.error('❌ Error completo:', error);
      console.error('❌ Error response:', error.response);
      
      // Mensajes de error más específicos
      let errorMessage = 'Email o contraseña incorrectos.';
      
      if (error.response) {
        console.log('Status:', error.response.status);
        if (error.response.status === 401) {
          errorMessage = '❌ Contraseña incorrecta. Verifica tus datos.';
        } else if (error.response.status === 404) {
          errorMessage = '❌ Usuario no encontrado. Verifica tu email.';
        } else if (error.response.data?.detail) {
          errorMessage = error.response.data.detail;
        }
      } else if (error.message === 'Network Error') {
        errorMessage = '❌ Error de conexión. Verifica tu internet.';
      } else if (error.message) {
        errorMessage = `❌ Error: ${error.message}`;
      }
      
      toast({
        title: 'Error al iniciar sesión',
        description: errorMessage,
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-blue-100 py-12 px-4">
      <div className="max-w-md mx-auto">
        <div className="text-center mb-8">
          <img 
            src="https://customer-assets.emergentagent.com/job_morabio-web/artifacts/o1ie7x5d_LogoMoraBioNegro.jpg" 
            alt="MoraBioVet" 
            className="h-20 mx-auto mb-4"
          />
          <h1 className="text-3xl font-bold text-[#1e3a8a] mb-2">Iniciar sesión</h1>
          <p className="text-gray-600">Accede a tu cuenta de MoraBioVet</p>
        </div>

        <Card className="shadow-xl">
          <CardHeader>
            <CardTitle className="text-center text-[#1e3a8a]">Portal de clientes</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Correo electrónico
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                  <Input
                    type="email"
                    placeholder="tu@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Contraseña
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-10 pr-10"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
              </div>

              <div className="flex items-center justify-between text-sm">
                <label className="flex items-center">
                  <input type="checkbox" className="mr-2" />
                  <span className="text-gray-600">Recordarme</span>
                </label>
                <Link to="/recuperar-password" className="text-[#1e40af] hover:text-[#1e3a8a]">
                  ¿Olvidaste tu contraseña?
                </Link>
              </div>

              <Button
                type="submit"
                className="w-full bg-[#1e40af] hover:bg-[#1e3a8a] text-white py-6 text-lg"
                disabled={isLoading}
              >
                {isLoading ? 'Iniciando sesión...' : 'Iniciar sesión'}
              </Button>

              <div className="text-center text-sm text-gray-600">
                ¿No tienes cuenta?{' '}
                <Link to="/registro" className="text-[#1e40af] hover:text-[#1e3a8a] font-semibold">
                  Regístrate aquí
                </Link>
              </div>
            </form>
          </CardContent>
        </Card>

        <div className="mt-6 text-center text-sm text-gray-600">
          <p>Al iniciar sesión, aceptas nuestros</p>
          <a href="#" className="text-[#1e40af] hover:underline">Términos y condiciones</a>
          {' y '}
          <a href="#" className="text-[#1e40af] hover:underline">Política de privacidad</a>
        </div>
      </div>
    </div>
  );
};

export default Login;